import axios from 'axios';

// Z-API Configuration
// IMPORTANT: These credentials determine the SENDER (psychologist's WhatsApp)
// The Z-API instance must be connected to the psychologist's WhatsApp number
const ZAPI_INSTANCE_ID = import.meta.env?.VITE_ZAPI_INSTANCE_ID;
const ZAPI_TOKEN = import.meta.env?.VITE_ZAPI_TOKEN;
const ZAPI_CLIENT_TOKEN = import.meta.env?.VITE_ZAPI_CLIENT_TOKEN;

// Create axios instance with Z-API configuration
// This baseURL determines the SENDER (origin) of all WhatsApp messages
// Messages will ALWAYS be sent from the psychologist's WhatsApp number
// that is connected to this Z-API instance
const zapiClient = axios?.create({
  baseURL: `https://api.z-api.io/instances/${ZAPI_INSTANCE_ID}/token/${ZAPI_TOKEN}`,
  headers: {
    'Content-Type': 'application/json',
    'client-token': ZAPI_CLIENT_TOKEN
  }
});

/**
 * Format phone number for WhatsApp (remove special characters, add country code if needed)
 * @param {string} phone - Phone number to format
 * @returns {string} - Formatted phone number with @c.us suffix
 */
const formatWhatsAppNumber = (phone) => {
  if (!phone) return null;
  
  // Remove all non-numeric characters
  let cleaned = phone?.replace(/\D/g, '');
  
  // If doesn't start with country code, assume Brazil (+55)
  if (!cleaned?.startsWith('55') && cleaned?.length <= 11) {
    cleaned = '55' + cleaned;
  }
  
  // Add WhatsApp suffix for individual chats
  return `${cleaned}@c.us`;
};

/**
 * Format appointment date properly to avoid timezone issues
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date} - Local date object
 */
const parseAppointmentDate = (dateString) => {
  // Split the date string to avoid timezone conversion issues
  const [year, month, day] = dateString?.split('-')?.map(Number);
  // Create date in local timezone (month is 0-indexed)
  return new Date(year, month - 1, day);
};

/**
 * Send WhatsApp message via Z-API
 * IMPORTANT: The SENDER (origin) is ALWAYS the psychologist's WhatsApp number
 * configured in the Z-API instance (ZAPI_INSTANCE_ID + ZAPI_TOKEN).
 * 
 * @param {string} phone - RECIPIENT phone number (patient's WhatsApp)
 * @param {string} message - Message text to send
 * @returns {Promise<Object>} - API response
 */
export const sendWhatsAppMessage = async (phone, message) => {
  try {
    const formattedPhone = formatWhatsAppNumber(phone);
    
    if (!formattedPhone) {
      throw new Error('Número de telefone inválido');
    }

    if (!ZAPI_INSTANCE_ID || !ZAPI_TOKEN || !ZAPI_CLIENT_TOKEN) {
      throw new Error('Credenciais Z-API não configuradas. Configure VITE_ZAPI_INSTANCE_ID, VITE_ZAPI_TOKEN e VITE_ZAPI_CLIENT_TOKEN no arquivo .env');
    }

    console.log('📞 Enviando para:', formattedPhone);
    console.log('🔐 Instance ID:', ZAPI_INSTANCE_ID);
    console.log('🔐 Client Token configurado:', !!ZAPI_CLIENT_TOKEN);
    console.log('📤 Remetente: Psicólogo (número configurado na instância Z-API)');

    // The 'phone' parameter here is the RECIPIENT (patient)
    // The SENDER is determined by the Z-API instance configuration
    const response = await zapiClient?.post('/send-messages', {
      phone: formattedPhone,
      message: message
    });

    console.log('✅ Resposta da API:', response?.data);

    return {
      success: true,
      data: response?.data,
      phone: formattedPhone
    };
  } catch (error) {
    console.error('❌ Erro detalhado:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
      config: {
        url: error?.config?.url,
        method: error?.config?.method,
        data: error?.config?.data
      }
    });

    return {
      success: false,
      error: error?.response?.data?.message || error?.message || 'Erro ao enviar mensagem',
      details: error?.response?.data,
      phone: phone
    };
  }
};

/**
 * Send appointment scheduled notification
 * @param {Object} appointment - Appointment data
 * @param {Object} patient - Patient data
 * @returns {Promise<Object>} - Send result
 */
export const sendAppointmentScheduledNotification = async (appointment, patient) => {
  // Use proper date parsing to avoid timezone issues
  const appointmentDate = parseAppointmentDate(appointment?.appointment_date);
  const formattedDate = appointmentDate?.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
  
  let message = `🗓️ *Consulta Agendada*\n\n` +
    `Olá ${patient?.full_name}!\n\n` +
    `Sua consulta foi agendada com sucesso:\n\n` +
    `📅 Data: ${formattedDate}\n` +
    `⏰ Horário: ${appointment?.start_time} horário de Brasília (GMT-3)\n\n` +
    `Você receberá um lembrete algumas horas antes da consulta.\n\n` +
    `Até breve! 👋`;

  return await sendWhatsAppMessage(patient?.whatsapp_number || patient?.phone, message);
};

/**
 * Send appointment status change notification
 * @param {Object} appointment - Appointment data
 * @param {Object} patient - Patient data
 * @param {string} oldStatus - Previous status
 * @param {string} newStatus - New status
 * @returns {Promise<Object>} - Send result
 */
export const sendAppointmentStatusChangeNotification = async (
  appointment,
  patient,
  oldStatus,
  newStatus
) => {
  const statusMessages = {
    confirmed: '✅ *Consulta Confirmada*',
    cancelled: '❌ *Consulta Cancelada*',
    completed: '✓ *Consulta Concluída*',
    in_progress: '▶️ *Consulta em Andamento*',
    no_show: '⚠️ *Falta Registrada*'
  };

  // Use proper date parsing to avoid timezone issues
  const appointmentDate = parseAppointmentDate(appointment?.appointment_date);
  const formattedDate = appointmentDate?.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  // Handle appointment modification (date/time change with same status)
  if (oldStatus === newStatus && newStatus === 'scheduled') {
    let message = `🔄 *Consulta Modificada*\n\n` +
      `Olá ${patient?.full_name}!\n\n` +
      `Sua consulta teve alterações:\n\n` +
      `📅 Nova Data: ${formattedDate}\n` +
      `⏰ Novo Horário: ${appointment?.start_time} horário de Brasília (GMT-3)\n\n` +
      `Por favor, confirme sua disponibilidade para o novo horário.\n\n` +
      `Qualquer dúvida, entre em contato! 📞`;

    return await sendWhatsAppMessage(patient?.whatsapp_number || patient?.phone, message);
  }

  const statusMessage = statusMessages?.[newStatus] || `Status atualizado para: ${newStatus}`;

  let message = `${statusMessage}\n\n` +
    `Olá ${patient?.full_name}!\n\n` +
    `📅 Data: ${formattedDate}\n` +
    `⏰ Horário: ${appointment?.start_time} horário de Brasília (GMT-3)\n`;

  // Add specific messages based on status
  if (newStatus === 'cancelled' && appointment?.cancellation_reason) {
    message += `\n📝 Motivo: ${appointment?.cancellation_reason}\n`;
    message += `\nSe desejar reagendar, entre em contato conosco. 📞`;
  }

  if (newStatus === 'confirmed') {
    message += `\nSua presença está confirmada! Nos vemos em breve. 👋`;
  }

  return await sendWhatsAppMessage(patient?.whatsapp_number || patient?.phone, message);
};

/**
 * Send appointment reminder
 * @param {Object} appointment - Appointment data
 * @param {Object} patient - Patient data
 * @returns {Promise<Object>} - Send result
 */
export const sendAppointmentReminder = async (appointment, patient) => {
  // Use proper date parsing to avoid timezone issues
  const appointmentDate = parseAppointmentDate(appointment?.appointment_date);
  const formattedDate = appointmentDate?.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  let message = `⏰ *Lembrete de Consulta*\n\n` +
    `Olá ${patient?.full_name}!\n\n` +
    `Este é um lembrete da sua consulta:\n\n` +
    `📅 Data: ${formattedDate}\n` +
    `⏰ Horário: ${appointment?.start_time} horário de Brasília (GMT-3)\n\n` +
    `Por favor, responda este mensagem com:\n` +
    `✅ *SIM* para confirmar sua presença\n` +
    `❌ *NÃO* caso precise cancelar\n\n` +
    `Aguardamos sua confirmação! 🙏`;

  return await sendWhatsAppMessage(patient?.whatsapp_number || patient?.phone, message);
};

/**
 * Check if reminder should be sent for an appointment
 * @param {Object} appointment - Appointment data
 * @param {number} hoursBeforeAppointment - Hours before appointment to send reminder
 * @returns {boolean} - Whether reminder should be sent
 */
export const shouldSendReminder = (appointment, hoursBeforeAppointment = 3) => {
  // Don't send if already sent
  if (appointment?.reminder_sent) {
    return false;
  }

  // Don't send for cancelled or completed appointments
  if (['cancelled', 'completed', 'no_show']?.includes(appointment?.status)) {
    return false;
  }

  const now = new Date();
  const appointmentDateTime = new Date(
    `${appointment.appointment_date}T${appointment.start_time}`
  );
  
  const hoursUntilAppointment = (appointmentDateTime - now) / (1000 * 60 * 60);
  
  // Send if appointment is within the reminder window
  return hoursUntilAppointment > 0 && hoursUntilAppointment <= hoursBeforeAppointment;
};

export default {
  sendWhatsAppMessage,
  sendAppointmentScheduledNotification,
  sendAppointmentStatusChangeNotification,
  sendAppointmentReminder,
  shouldSendReminder,
  formatWhatsAppNumber
};